import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Building2, CreditCard, FileText, ShieldCheck, CheckCircle2, ArrowLeft, ArrowRight, Loader2, AlertTriangle, XCircle } from 'lucide-react';
import { BusinessInfoStep, BankDetailsStep, DeclarationsStep } from '../components/kyc/KYCFormSteps';
import { DocumentCard } from '../components/kyc/DocumentCard';
import { ENTITY_TYPES, kycMeta, normalizeKyc } from '../lib/kyc';

const STEPS = [
  { n: 1, title: 'Business Information', icon: Building2, desc: 'Tell us about your business' },
  { n: 2, title: 'Bank Details', icon: CreditCard, desc: 'Settlement / verification bank account' },
  { n: 3, title: 'KYC Documents', icon: FileText, desc: 'Upload documents required for your entity type' },
  { n: 4, title: 'Review & Submit', icon: ShieldCheck, desc: 'Confirm your details and submit for verification' },
];

const emptyDirector = () => ({ name: '', pan_number: '', din_number: '' });

const toForm = (d = {}) => ({
  business_name: d.business_name || '', entity_type: d.entity_type || '', pan_number: d.pan_number || '',
  gst_number: d.gst_number || '', business_description: d.business_description || '', website_url: d.website_url || '',
  app_url: d.app_url || '', expected_monthly_volume: d.expected_monthly_volume ?? '', refund_policy_url: d.refund_policy_url || '',
  bank_details: { account_holder_name: '', account_number: '', ifsc_code: '', bank_name: '', ...(d.bank_details || {}) },
  directors: d.directors?.length ? d.directors : [emptyDirector()], ubo_declaration: d.ubo_declaration || '',
});

export default function KYCPage() {
  const { api, refreshMerchant } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState(toForm());
  const [decl, setDecl] = useState({ agreement_accepted: false, aml_declaration_accepted: false, risk_disclosure_accepted: false });

  const load = useCallback(async () => {
    const r = await api.get('/merchants/me/kyc/status');
    setState(r.data);
    return r.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const s = await load();
        const st = normalizeKyc(s.status);
        if (['SUBMITTED', 'UNDER_REVIEW', 'APPROVED'].includes(st)) { navigate('/kyc/status', { replace: true }); return; }
        setForm(toForm(s.details));
        if (st === 'REUPLOAD_REQUIRED') setStep(3);
        else if (st === 'DRAFT' || st === 'REJECTED') setStep(s.businessDetailsComplete ? 3 : 1);
      } catch (e) {
        toast.error('Unable to load KYC status');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setBank = (k, v) => setForm(p => ({ ...p, bank_details: { ...p.bank_details, [k]: v } }));
  const setDirector = (i, k, v) => setForm(p => { const d = [...p.directors]; d[i] = { ...d[i], [k]: v }; return { ...p, directors: d }; });

  const validate = () => {
    if (step === 1) {
      if (!form.business_name || !form.entity_type || !form.pan_number || !form.business_description) { toast.error('Please fill all required fields'); return false; }
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number)) { toast.error('Invalid PAN number format'); return false; }
    }
    if (step === 2) {
      const b = form.bank_details;
      if (!b.account_holder_name || !b.account_number || !b.ifsc_code || !b.bank_name) { toast.error('Please fill all bank details'); return false; }
      if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(b.ifsc_code)) { toast.error('Invalid IFSC code'); return false; }
    }
    if (step === 3 && state?.missingDocuments?.length) { toast.error('Please upload all required documents'); return false; }
    if (step === 3 && state?.flaggedDocuments?.length) { toast.error('Please replace the documents flagged by our team'); return false; }
    return true;
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      await api.post('/merchants/onboard', {
        ...form,
        expected_monthly_volume: parseFloat(form.expected_monthly_volume) || 0,
        directors: form.directors.filter(d => d.name && d.pan_number),
        ...decl,
      });
      await load();
      await refreshMerchant();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save details');
      return false;
    } finally { setSaving(false); }
  };

  const next = async () => {
    if (!validate()) return;
    if (step === 2 && !(await saveDetails())) return;
    setStep(s => s + 1);
  };

  const submit = async () => {
    if (!decl.agreement_accepted || !decl.aml_declaration_accepted || !decl.risk_disclosure_accepted) { toast.error('Please accept all declarations'); return; }
    setSaving(true);
    try {
      const r = await api.post('/merchants/me/kyc/submit', decl);
      await refreshMerchant();
      setSubmitted(r.data);
      toast.success('KYC submitted successfully');
      setTimeout(() => navigate('/kyc/status', { replace: true }), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit KYC');
    } finally { setSaving(false); }
  };

  const onDocUploaded = async () => { await load(); };

  if (loading) return <div className="flex items-center justify-center min-h-[50vh] text-muted-foreground" data-testid="kyc-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading KYC status…</div>;
  if (!state) return null;

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto py-12" data-testid="kyc-submitted-screen">
        <Card className="card-grid text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center"><CheckCircle2 className="h-8 w-8 text-green-500" /></div>
            <h2 className="text-2xl font-heading font-bold">KYC Submitted Successfully</h2>
            <p className="text-muted-foreground text-sm">Your documents have been submitted to our verification team.</p>
            <div className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-sm bg-amber-500/10 text-amber-600 border border-amber-500/30"><span className="w-2 h-2 rounded-full bg-amber-500" /> Under Review</div>
            {submitted.applicationId && <p className="text-xs text-muted-foreground font-mono" data-testid="kyc-application-id">Application ID: {submitted.applicationId}</p>}
            <p className="text-xs text-muted-foreground">You will be notified once verification is completed. Redirecting to status…</p>
            <Button onClick={() => navigate('/kyc/status')} className="rounded-sm" data-testid="go-to-status-btn">View KYC Status</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = normalizeKyc(state.status);
  const meta = kycMeta(status);
  const current = STEPS[step - 1];
  const entityLabel = ENTITY_TYPES.find(e => e.value === (state.entityType || form.entity_type))?.label;
  const headline = { NOT_STARTED: 'Complete Your KYC', DRAFT: 'KYC In Progress', REJECTED: 'KYC Rejected — Update & Resubmit', REUPLOAD_REQUIRED: 'KYC Update Required' }[status] || 'KYC Verification';
  const subline = {
    NOT_STARTED: 'Your merchant account is ready. Complete your KYC verification to unlock payouts.',
    DRAFT: "You have started your KYC application but haven't submitted it yet.",
    REJECTED: 'Your KYC application was not approved. Update your details or documents and resubmit.',
    REUPLOAD_REQUIRED: 'Our verification team has requested updated documents.',
  }[status];

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-stagger" data-testid="kyc-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold" data-testid="kyc-headline">{headline}</h1>
          <p className="text-muted-foreground text-sm mt-1">{subline}</p>
          {state.mid && <p className="text-xs text-muted-foreground font-mono mt-1">Merchant ID: {state.mid}{state.applicationId ? ` · Application: ${state.applicationId}` : ''}</p>}
        </div>
        <span className={`inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-sm border ${meta.badge}`} data-testid="kyc-status-badge">
          <span className={`w-2 h-2 rounded-full ${meta.dot}`} /> {meta.label}
        </span>
      </div>

      {state.remarks && (
        <Card className={`${status === 'REJECTED' ? 'border-red-500/40 bg-red-500/5' : 'border-orange-500/40 bg-orange-500/5'}`} data-testid="kyc-remarks-banner">
          <CardContent className="py-4 flex items-start gap-3">
            {status === 'REJECTED' ? <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" /> : <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />}
            <div>
              <p className="font-medium text-sm">{status === 'REJECTED' ? 'Reason for rejection' : 'Requested update'}</p>
              <p className="text-sm text-muted-foreground mt-0.5" data-testid="kyc-remarks-text">{state.remarks}</p>
              {state.flaggedDocuments?.length > 0 && <p className="text-xs text-muted-foreground mt-1">Documents to replace: {state.flaggedDocuments.map(k => state.documents.find(d => d.doc_type === k)?.doc_label || k).join(', ')}</p>}
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <div className="flex justify-between items-center mb-3">
          {STEPS.map(s => (
            <button key={s.n} type="button" onClick={() => s.n < step && setStep(s.n)}
              className={`flex flex-col items-center flex-1 ${s.n === step ? 'text-primary' : s.n < step ? 'text-green-500' : 'text-muted-foreground'}`} data-testid={`kyc-step-${s.n}`}>
              <div className={`w-9 h-9 rounded-sm flex items-center justify-center mb-1.5 border-2 transition-colors ${s.n === step ? 'bg-primary/10 border-primary' : s.n < step ? 'bg-green-500/10 border-green-500' : 'border-border'}`}>
                <s.icon className="h-4 w-4" strokeWidth={1.5} />
              </div>
              <span className="text-xs font-medium hidden sm:block">{s.title}</span>
            </button>
          ))}
        </div>
        <Progress value={(step / STEPS.length) * 100} className="h-1" />
      </div>

      <Card className="card-grid">
        <CardHeader>
          <CardTitle className="text-xl font-heading">{current.title}</CardTitle>
          <CardDescription>{current.desc}{step === 3 && entityLabel ? ` — ${entityLabel}` : ''}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {step === 1 && <BusinessInfoStep form={form} onChange={set} onDirectorChange={setDirector} onAddDirector={() => set('directors', [...form.directors, emptyDirector()])} />}
          {step === 2 && <BankDetailsStep bank={form.bank_details} onChange={setBank} />}
          {step === 3 && (
            <div className="space-y-3" data-testid="kyc-documents-step">
              {state.requiredDocuments.length === 0 && <p className="text-sm text-muted-foreground">Save your business information first to see the documents required for your entity type.</p>}
              {state.requiredDocuments.map(item => <DocumentCard key={item.doc_type} item={item} canUpload={state.canUpload} onUploaded={onDocUploaded} />)}
              {state.optionalDocuments.length > 0 && <p className="text-xs uppercase tracking-wider text-muted-foreground pt-2">Optional</p>}
              {state.optionalDocuments.map(item => <DocumentCard key={item.doc_type} item={item} canUpload={state.canUpload} onUploaded={onDocUploaded} />)}
              <p className="text-xs text-muted-foreground pt-1">
                {state.missingDocuments.length ? `${state.missingDocuments.length} required document(s) pending.` : 'All required documents have been uploaded.'}
              </p>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-5" data-testid="kyc-review-step">
              <div className="p-4 rounded-sm border border-green-500/30 bg-green-500/5 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                <div><p className="font-medium text-sm">All required documents have been uploaded</p><p className="text-xs text-muted-foreground">Review your details below and submit your KYC for verification.</p></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {[['Business', form.business_name], ['Entity Type', entityLabel], ['PAN', form.pan_number], ['GST', form.gst_number || '—'],
                  ['Bank', form.bank_details.bank_name], ['Account', `•••• ${String(form.bank_details.account_number).slice(-4)}`], ['IFSC', form.bank_details.ifsc_code],
                  ['Documents', `${state.documents.length} uploaded`]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-border/40 py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right truncate">{v}</span></div>
                ))}
              </div>
              <DeclarationsStep values={decl} onChange={(k, v) => setDecl(p => ({ ...p, [k]: v }))} />
            </div>
          )}

          <div className="flex justify-between pt-5 border-t border-border/50">
            <Button variant="outline" onClick={step === 1 ? () => navigate('/dashboard') : () => setStep(s => s - 1)} className="rounded-sm" data-testid="kyc-back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> {step === 1 ? 'Back to Dashboard' : 'Back'}
            </Button>
            {step < STEPS.length ? (
              <Button onClick={next} disabled={saving} className="rounded-sm glow-primary" data-testid="kyc-next-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{step === 2 ? 'Save & Continue' : 'Next'} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={saving || !state.canSubmit} className="rounded-sm glow-primary" data-testid="kyc-submit-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : <><ShieldCheck className="h-4 w-4 mr-2" /> Submit KYC for Review</>}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
